/**
 * Production-build replacement for `react/jsx-runtime`. WordPress exposes React as the UMD
 * global `window.React`, which has `createElement` but not the jsx-runtime entry point, so the
 * automatic JSX transform is mapped onto createElement here. Dev server and tests use the real
 * runtime from node_modules (see vite.config.ts).
 */
import * as React from 'react';

type Props = Record<string, unknown> & { children?: unknown };
type AnyType = Parameters<typeof React.createElement>[0];

const createElement = React.createElement as (type: AnyType, props: unknown, ...children: unknown[]) => React.ReactElement;

function build(type: AnyType, props: Props | null, key: React.Key | undefined, staticChildren: boolean): React.ReactElement {
  const { children, ...rest } = props ?? {};
  const finalProps = key === undefined ? rest : { ...rest, key };
  if (children === undefined) return createElement(type, finalProps);
  // jsxs() receives a static array of children; jsx() receives one child (which may itself be a keyed list).
  if (staticChildren && Array.isArray(children)) return createElement(type, finalProps, ...children);
  return createElement(type, finalProps, children);
}

export const Fragment = React.Fragment;
export const jsx = (type: AnyType, props: Props | null, key?: React.Key) => build(type, props, key, false);
export const jsxs = (type: AnyType, props: Props | null, key?: React.Key) => build(type, props, key, true);
export const jsxDEV = (type: AnyType, props: Props | null, key?: React.Key) => build(type, props, key, false);
